// glideRing.ts — Compute a terrain-clipped, wind-corrected emergency
// glide-ring polygon around ownship.
//
// Approach: cast rays from ownship in N bearings (default 72 = every 5°).
// For each bearing, "march" out from ownship in small steps, dropping
// altitude as we go according to the best glide ratio plus the
// per-altitude wind solution along that bearing. Stop the ray at the
// first step where the glide-altitude line dips below terrain + safety
// buffer. The collection of (bearing, distance) tuples becomes the
// reachable polygon.
//
// Units: all internal math is canonical (knots / feet / NM); UI is
// responsible for unit display.
//
// Performance: a typical 72-bearing × ~50-step march is ~3,600 terrain
// samples per recompute. With terrain tiles cached in memory (see
// terrainSampler.ts) this finishes in ~50ms on a desktop, 100-200ms
// on an iPad after the first run.

import type { WindsProfile, ProfileHour, ProfileLevel } from './winds';

// ─── Constants ────────────────────────────────────────────────────────────

const EARTH_RADIUS_NM = 3440.065;
const FT_PER_NM = 6076.115;

// ─── Geo helpers ──────────────────────────────────────────────────────────

function destinationPoint(
  lat: number,
  lon: number,
  distNm: number,
  bearingDegTrue: number,
): [lon: number, lat: number] {
  const dR = distNm / EARTH_RADIUS_NM;
  const latR = (lat * Math.PI) / 180;
  const lonR = (lon * Math.PI) / 180;
  const bearR = (bearingDegTrue * Math.PI) / 180;
  const lat2 = Math.asin(
    Math.sin(latR) * Math.cos(dR) + Math.cos(latR) * Math.sin(dR) * Math.cos(bearR),
  );
  const lon2 =
    lonR +
    Math.atan2(
      Math.sin(bearR) * Math.sin(dR) * Math.cos(latR),
      Math.cos(dR) - Math.sin(latR) * Math.sin(lat2),
    );
  return [(lon2 * 180) / Math.PI, (lat2 * 180) / Math.PI];
}

// ─── Wind interpolation (winds.ts shape) ──────────────────────────────────

/**
 * Pick the forecast hour from `profile` whose `validTime` is closest to
 * `target`. Returns null when the profile has no hours.
 */
export function pickClosestProfileHour(
  profile: WindsProfile | null,
  target: Date | number = Date.now(),
): ProfileHour | null {
  if (!profile?.hours?.length) return null;
  const tMs = typeof target === 'number' ? target : target.getTime();
  let best: ProfileHour | null = null;
  let bestDelta = Infinity;
  for (const h of profile.hours) {
    const d = Math.abs(new Date(h.validTime).getTime() - tMs);
    if (d < bestDelta) {
      best = h;
      bestDelta = d;
    }
  }
  return best;
}

/**
 * Linear-interpolate the wind dir/speed at `altFt` within a single hour.
 * Clamps at the extremes (no extrapolation). Returns null when `hour`
 * has no levels.
 */
export function interpolateWindAtAlt(
  hour: ProfileHour | null,
  altFt: number,
): { dirFromTrue: number; speedKt: number } | null {
  if (!hour?.levels?.length) return null;
  const sorted: ProfileLevel[] = [...hour.levels].sort((a, b) => a.altFt - b.altFt);
  if (altFt <= sorted[0].altFt) {
    return { dirFromTrue: sorted[0].dirFromTrue, speedKt: sorted[0].speedKt };
  }
  if (altFt >= sorted[sorted.length - 1].altFt) {
    const top = sorted[sorted.length - 1];
    return { dirFromTrue: top.dirFromTrue, speedKt: top.speedKt };
  }
  for (let i = 0; i < sorted.length - 1; i++) {
    const lo = sorted[i];
    const hi = sorted[i + 1];
    if (altFt >= lo.altFt && altFt <= hi.altFt) {
      const t = (altFt - lo.altFt) / (hi.altFt - lo.altFt);
      const dDeg = ((hi.dirFromTrue - lo.dirFromTrue + 540) % 360) - 180;
      const dir = (lo.dirFromTrue + dDeg * t + 360) % 360;
      return { dirFromTrue: dir, speedKt: lo.speedKt + (hi.speedKt - lo.speedKt) * t };
    }
  }
  return null;
}

// ─── Glide ring core ──────────────────────────────────────────────────────

export interface GlideRingInput {
  /** Ownship lat/lon. */
  lat: number;
  lon: number;
  /** Ownship altitude MSL in feet. */
  altMslFt: number;
  /** Best glide TAS in knots (storage canonical unit). */
  bestGlideKt: number;
  /** Lift-to-drag ratio (e.g. 9 for C182). */
  bestGlideRatio: number;
  /** Altitude buffer above terrain at touchdown, in feet. Default 100. */
  safetyBufferFt?: number;
  /** Number of bearings to sample (default 72 = every 5°). */
  bearings?: number;
  /** March step in NM (default 0.1). */
  stepNm?: number;
  /** Maximum reach in NM (safety cap; default 30). */
  maxReachNm?: number;
  /**
   * Terrain sampler: returns elevation in feet at (lat, lon).
   * Async; the glide-ring caller awaits the full ring computation.
   */
  sampleTerrainFt: (lat: number, lon: number) => Promise<number>;
  /** Winds-aloft profile near ownship (use closest hour). Optional; falls back to no-wind. */
  windsProfile?: WindsProfile | null;
}

export interface GlideRingResult {
  /** Closed polygon ring as [lon, lat] pairs (first === last). */
  ring: Array<[number, number]>;
  /** Per-bearing reach distance in NM. Same length as `bearings`. */
  reachesNm: number[];
  /** Bearings used (true degrees). */
  bearingsDegTrue: number[];
  /** True if every bearing's reach was 0 (no glide possible — likely AGL ≤ 0). */
  empty: boolean;
}

/**
 * Compute a glide-ring polygon. Returns a closed ring of [lon, lat]
 * pairs suitable for direct use as a MapLibre LineString.
 *
 * Wind handling: the wind at each step's altitude is sampled from the
 * supplied profile, giving "different wind solutions per altitude band
 * as you descend." When `windsProfile` is null, the calculation is
 * zero-wind.
 *
 * Algorithm per bearing θ:
 *   alt = altMslFt
 *   distNm = 0
 *   while distNm < maxReachNm:
 *     wind = interpolateWindAtAlt(profileHour, alt)
 *     // Tailwind component on this bearing (positive = pushed along):
 *     wcomp = wind.speedKt * cos((wind.dirFromTrue + 180) - θ) // wind dir-FROM → dir-TO
 *     gs = bestGlideKt + wcomp
 *     if gs <= 0: break  // can't penetrate the wind even with best glide
 *     // Vertical descent in this step:
 *     // sink rate (ft/NM) = FT_PER_NM / bestGlideRatio  (still-air L/D in TAS terms,
 *     //   approximated for tailwind/headwind by adjusting TAS → GS ratio below)
 *     // Effective ft/NM groundspeed-relative:
 *     //   sink_per_ground_nm = (FT_PER_NM / bestGlideRatio) * (bestGlideKt / gs)
 *     sinkPerGroundNm = (FT_PER_NM / bestGlideRatio) * (bestGlideKt / gs)
 *     alt -= sinkPerGroundNm * stepNm
 *     distNm += stepNm
 *     elev = sampleTerrainFt(at distNm on bearing θ from origin)
 *     if alt <= elev + safetyBufferFt: break
 *   reach[θ] = distNm
 */
export async function computeGlideRing(input: GlideRingInput): Promise<GlideRingResult> {
  const {
    lat,
    lon,
    altMslFt,
    bestGlideKt,
    bestGlideRatio,
    safetyBufferFt = 100,
    bearings = 72,
    stepNm = 0.1,
    maxReachNm = 30,
    sampleTerrainFt,
    windsProfile = null,
  } = input;

  // Guard: bad inputs.
  if (!Number.isFinite(altMslFt) || bestGlideKt <= 0 || bestGlideRatio <= 0) {
    const bearingsDegTrue = Array.from({ length: bearings }, (_, i) => (i * 360) / bearings);
    return {
      ring: [[lon, lat], [lon, lat]],
      reachesNm: bearingsDegTrue.map(() => 0),
      bearingsDegTrue,
      empty: true,
    };
  }

  const hour = pickClosestProfileHour(windsProfile);
  const bearingsDegTrue = Array.from({ length: bearings }, (_, i) => (i * 360) / bearings);

  // Compute each bearing in parallel. Each bearing's march is sequential
  // (we need the altitude from step N-1 to choose the wind at step N),
  // but bearings are independent.
  const reachesNm = await Promise.all(
    bearingsDegTrue.map(async (theta) => {
      let alt = altMslFt;
      let dist = 0;
      // Loop guard: at most maxReachNm / stepNm steps.
      const maxSteps = Math.ceil(maxReachNm / stepNm);
      for (let i = 0; i < maxSteps; i++) {
        const wind = hour ? interpolateWindAtAlt(hour, alt) : null;
        // dirFromTrue is the direction the wind is coming FROM. The
        // wind-along-bearing component is:
        //   tailwind = -windSpeed * cos(dirFromTrue - theta)
        // (when wind FROM = theta, we're headed straight into it →
        //  tailwind = -windSpeed; when wind FROM = theta+180, fully
        //  tailwind = +windSpeed.)
        let tailwindKt = 0;
        if (wind) {
          const delta = ((wind.dirFromTrue - theta) * Math.PI) / 180;
          tailwindKt = -wind.speedKt * Math.cos(delta);
        }
        const gs = bestGlideKt + tailwindKt;
        if (gs <= 0) break; // ray pinned: headwind exceeds best glide TAS

        // Descent per ground NM:
        //   in still air: sink_per_ground_nm = FT_PER_NM / glide_ratio
        //   in wind: scale by (TAS / GS) — we drop the same vertical
        //   feet per unit AIR distance, so per unit GROUND distance we
        //   drop (TAS / GS) × that.
        const sinkPerGroundNm = (FT_PER_NM / bestGlideRatio) * (bestGlideKt / gs);
        const nextAlt = alt - sinkPerGroundNm * stepNm;
        const nextDist = dist + stepNm;

        // Sample terrain at the END of this step.
        const [stepLon, stepLat] = destinationPoint(lat, lon, nextDist, theta);
        const elevFt = await sampleTerrainFt(stepLat, stepLon);
        const elevSafe = Number.isFinite(elevFt) ? elevFt : -Infinity; // missing tile → treat as ocean
        if (nextAlt <= elevSafe + safetyBufferFt) {
          // The ray ends inside this step. Use the nextDist as the
          // reach (we crossed the terrain plane within this step;
          // sub-step precision isn't worth the complexity at z=11).
          dist = nextDist;
          break;
        }
        alt = nextAlt;
        dist = nextDist;
      }
      return dist;
    }),
  );

  // Build the polygon ring. If every reach is 0, return an empty ring.
  const empty = reachesNm.every((r) => r <= 0);
  if (empty) {
    return { ring: [[lon, lat], [lon, lat]], reachesNm, bearingsDegTrue, empty: true };
  }

  const ring: Array<[number, number]> = bearingsDegTrue.map((theta, i) => {
    const r = reachesNm[i];
    if (r <= 0) return [lon, lat];
    const [px, py] = destinationPoint(lat, lon, r, theta);
    return [px, py];
  });
  // Close the ring.
  ring.push(ring[0]);
  return { ring, reachesNm, bearingsDegTrue, empty: false };
}

/** Bounding box (minLon, minLat, maxLon, maxLat) enclosing a ring. */
export function ringBbox(
  ring: Array<[number, number]>,
): [minLon: number, minLat: number, maxLon: number, maxLat: number] {
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;
  for (const [x, y] of ring) {
    if (x < minLon) minLon = x;
    if (y < minLat) minLat = y;
    if (x > maxLon) maxLon = x;
    if (y > maxLat) maxLat = y;
  }
  return [minLon, minLat, maxLon, maxLat];
}

/**
 * Point-in-ring test using the ray-cast algorithm. The ring may be
 * any closed polygon (first === last). Used to filter airports within
 * the reachable glide region.
 */
export function pointInRing(
  lon: number,
  lat: number,
  ring: Array<[number, number]>,
): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect =
      yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}
